/*
 * Purpose: Mission workspace — map, operate, insights, settings — P3
 * Last modified: 2026-07-10 | Agent
 * Completeness: 100
 */
import { useEffect, useRef, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { SetupBanner } from '@/components/setup/SetupBanner';
import { MissionSubNav } from '@/components/mission/MissionSubNav';
import { MissionPanelContent } from '@/components/mission/MissionPanelContent';
import { MissionSectionView } from '@/components/mission/MissionSectionView';
import { WorkflowCreator } from '@/components/WorkflowCreator';
import { MissionWorkspaceProvider, useMissionWorkspace } from '@/contexts/MissionWorkspaceContext';
import { matchMissionSection } from '@/lib/missionNav';
import { useMissionMapToolsStickyTop } from '@/hooks/use-mission-map-tools-sticky-top';

function MissionMapRoute() {
  return (
    <div className="flex flex-1 flex-col min-h-0 p-2 sm:p-3">
      <MissionPanelContent panel="builder" mapShortcutsActive />
    </div>
  );
}

function MissionPageInner() {
  const [creatorOpen, setCreatorOpen] = useState(false);
  const { setOnNewFlow } = useMissionWorkspace();
  const { pathname } = useLocation();
  const onMap = matchMissionSection(pathname) === 'map';
  const mapChromeScrollRef = useRef<HTMLDivElement>(null);
  useMissionMapToolsStickyTop(mapChromeScrollRef);

  useEffect(() => {
    setOnNewFlow(() => setCreatorOpen(true));
    return () => setOnNewFlow(undefined);
  }, [setOnNewFlow]);

  if (onMap) {
    return (
      <div className="flex flex-col flex-1 min-h-0 h-full md:min-h-[calc(100dvh-3.25rem)] ui-page-hierarchy overflow-hidden">
        <div
          ref={mapChromeScrollRef}
          className="ab-mission-map-scroll shrink-0 min-h-0 overflow-y-auto overscroll-y-contain"
        >
          <MissionSubNav mapStickyChrome />
        </div>
        <SetupBanner />
        <MissionMapRoute />
        <WorkflowCreator open={creatorOpen} onOpenChange={setCreatorOpen} />
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 min-h-0 h-full md:min-h-[calc(100dvh-3.25rem)] ui-page-hierarchy">
      <MissionSubNav />
      <SetupBanner />
      <div className="flex flex-1 flex-col min-h-0">
        <Routes>
          <Route index element={<Navigate to="map" replace />} />
          <Route path="map" element={<MissionMapRoute />} />
          <Route path=":section/:panel" element={<MissionSectionView />} />
        </Routes>
      </div>
      <WorkflowCreator open={creatorOpen} onOpenChange={setCreatorOpen} />
    </div>
  );
}

export default function MissionPage() {
  return (
    <MissionWorkspaceProvider>
      <MissionPageInner />
    </MissionWorkspaceProvider>
  );
}
